<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { reactive, onMounted, computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import CategoryLayout from '@/Layouts/CategoryLayout.vue';
import ProductCarousel from '@/Components/Carousels/ProductCarousel.vue';
import ProductLinkList from '@/Components/ProductLinkList.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import BrandStrip from '@/Components/Category/BrandStrip.vue';
import { marked } from 'marked';
import { sanitizeHtml } from '@/Utils/sanitize';
import NyhedsBlok from '@/Components/NyhedsBlok.vue';

const page = usePage();

const nyheder = computed(() => page.props.nyheder || []);

const user = computed(
    () => page.props.auth.user
);

const breadcrumbs = computed(
    () => page.props.breadcrumbs
);

const pageTitle = computed(() => {
    return page.props.pageTitle;
});

const subCategories = computed(() => page.props.subCategories);

const popularCatProducts = computed(() => page.props.popularCatProducts);
const productLinks = computed(() => page.props.productLinks || []);
const currentCategory = computed(() => page.props.subCategory);

const seoData = computed(() => page.props.seo ?? {});

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
});

const state = reactive({
    loading: false,
});

const topSeoText = computed(() =>
    currentCategory.value?.top_seo_intro
        ? sanitizeHtml(marked.parse(currentCategory.value.top_seo_intro))
        : ''
);

/*
 * Mellemniveauet fik hverken prisoversigt, prisudvikling eller FAQ. De 304
 * sider her bestod af billeder og produktnavne, mens hoved- og
 * underunderkategorierne havde det hele. Tjenesterne er de samme.
 */
const prisoversigt = computed(() => page.props.prisoversigt || null);
const prisoversigtOverskrift = computed(() => page.props.prisoversigtOverskrift || '');
const prisudvikling = computed(() => page.props.prisudvikling || null);
const faq = computed(() => page.props.faq || []);

const bottomSeoText = computed(() =>
    currentCategory.value?.bottom_seo_intro
        ? sanitizeHtml(marked.parse(currentCategory.value.bottom_seo_intro))
        : ''
);

onMounted(async () => {

});
</script>

<template>
    <CategoryLayout>
        <div class="container-fluid text-center g-0">
            <BreadcrumbBar :breadcrumbs="breadcrumbs" />
            <!--
                Overskriften rammer prissøgningerne.

                h1 var kategorinavnet alene: "Bundkort", "Grafikkort", "Tv".
                De prisord folk faktisk søger på, er "tv priser", "billig tv",
                "tv tilbud" og "billig server", og ordet "priser" stod ikke i
                en eneste h1 på sitet.
            -->
            <h1 class="h3 mb-3 mt-3 text-start px-3">{{ currentCategory.name }} priser</h1>
            <BrandStrip v-if="page.props.categoryBrands && page.props.categoryBrands.length > 0" :brands="page.props.categoryBrands" class="mx-3 mb-3" />
            <div class="row g-0">
                <!--
                    col-2 uden noget mobil-alternativ gav en kolonne på cirka
                    66 px på en telefon, og så brød hvert ord bogstav for
                    bogstav: "Mi ni pc", "B ær bar lap top". De to andre
                    kategorisider har for længst responsive klasser.
                -->
                <div class="col-12 col-md-3 col-lg-2">
                    <div class="card rounded-0">
                        <div class="card-body">
                            <ul class="list-group list-group-flush sub-category-list">
                                <li v-for="category in subCategories" :key="category.id" class="list-group-item">
                                    <font-awesome-icon icon="fa-solid fa-angle-right" />&nbsp;<a :href="category.url">{{ category.name }}</a>
                                </li>
                            </ul>
                        </div> 
                    </div>  
                </div>
                <div class="col-12 col-md-9 col-lg-10 main-content-block p-2">
                    <div class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <div v-if="topSeoText && topSeoText.trim() !== ''" class="text-muted small" v-html="topSeoText"></div>
                            <div v-else-if="seoData.description" class="text-muted small">
                                {{ seoData.description }}
                            </div>
                        </div>
                    </div>

                    <!-- Samme raekkefoelge som hovedkategorien: svaret paa hvad
                         varerne koster staar foer billederne. -->
                    <!--
                      Hvad koster varerne her, og er priserne paa vej op eller
                      ned? Tallene er vores egne maalinger. Teksten staar i
                      HTML og ikke kun i skema-opmaerkning: et svar der kun
                      findes i JSON-LD kan Google afvise som skjult indhold, og
                      et menneske faar intet ud af det.
                    -->
                    <section v-if="prisoversigt" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <h2 class="h5 mb-3">{{ prisoversigtOverskrift }}</h2>
                            <p class="mb-0" style="color: var(--pc-text-secondary);">{{ prisoversigt.tekst }}</p>
                            <p v-if="prisudvikling" class="mb-0 mt-3" style="color: var(--pc-text-secondary);">{{ prisudvikling.tekst }}</p>
                        </div>
                    </section>

                    <div v-for="(category, name) in popularCatProducts">
                        <div class="card">
                            <div class="card-text">
                                <ProductCarousel :componentIdentifier="'carousel_' + name" :products="category" :headline="'Populære produkter i ' + name" :productsPrPage="4" />
                            </div>
                        </div>
                        <br/>
                    </div>


                    <section v-if="faq.length" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <h2 class="h5 mb-3">Ofte stillede spørgsmål</h2>
                            <div v-for="(q, i) in faq" :key="i" :class="i > 0 ? 'mt-3' : ''">
                                <h3 class="h6 mb-1">{{ q.spoergsmaal }}</h3>
                                <p class="mb-0" style="color: var(--pc-text-secondary);">{{ q.svar }}</p>
                            </div>
                        </div>
                    </section>

                    <ProductLinkList :products="productLinks" title="Populære produkter" />

                    <div v-if="bottomSeoText && bottomSeoText.trim() !== ''" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <div class="seo-long-text text-muted" v-html="bottomSeoText"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="container">
            <NyhedsBlok :nyheder="nyheder" overskrift="Nyheder i denne kategori" />
        </div>
    </CategoryLayout>
</template>

<style scoped>
.main-content-block {
    /*
     * Samme værn som i SubSubCategory: en Bootstrap-col kan ikke blive
     * smallere end sit indholds min-content, og en produktliste med en
     * ubrudt streng i et navn brækker så kolonnen ned under filtrene.
     */
    min-width: 0;
}

.sub-category-list {
    padding: 0;
    text-align: left;
}

.sub-category-list li {
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--pc-border);
}

/*
    På en telefon er en lodret liste af underkategorier spild af en hel
    skærm, før man overhovedet ser varerne. Den lægger sig i stedet som
    en række knapper der bryder over flere linjer.
*/
@media (max-width: 767.98px) {
    .sub-category-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .sub-category-list li {
        padding: 4px 10px;
        border: 1px solid var(--pc-border);
        border-radius: 999px;
        white-space: nowrap;
    }
}

.sub-category-list li a {
    color: var(--pc-accent);
    text-decoration: none;
}
</style>